import { css, html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import SignaturePad from "signature_pad";

@customElement("pg-signature-pad")
export class PGDSignaturePad extends LitElement {
  @property({ type: Number }) minWidth = 0.5;
  @property({ type: Number }) maxWidth = 2.5;
  @property({ type: String }) penColor = "#000000";
  @property({ type: Boolean }) showSave = undefined;
  @property({ type: Boolean }) showClear = undefined;
  @property({ type: String }) saveLabelBtn = "Save";
  @property({ type: String }) clearLabelBtn = "Clear";

  private signaturePad: SignaturePad | null = null;

  static styles = css`
    .signature-drawer {
      display: flex;
      justify-content: center;
      margin: 10px 20px 10px 20px;
    }

    .signature-pad {
      border-radius: 4px;
      border: 1px solid #868686;
      width: 100%;
      height: 157px;
    }
    button {
      border: 1px solid rgb(51, 51, 51);
      border-radius: 3px;
      line-height: 32px;
      font-size: 17px;
      padding: 4px 10px;
    }

    .signature-btn-save {
      color: #fff;
      background-color: rgb(0, 118, 255);
    }

    .signature-btn-clear {
      color: rgb(51, 51, 51);
      background-color: rgb(255, 255, 255);
    }
  `;

  generateSignaturePad() {
    const canvas = this.shadowRoot?.querySelector(".signature-pad") as HTMLCanvasElement | null;
    if (canvas) {
      if (this.signaturePad) {
        // If signaturePad already exists, update its properties
        this.signaturePad.minWidth = this.minWidth;
        this.signaturePad.maxWidth = this.maxWidth;
        this.signaturePad.penColor = this.penColor;
      } else {
        // If signaturePad doesn't exist, create a new one
        this.signaturePad = new SignaturePad(canvas, {
          minWidth: this.minWidth,
          maxWidth: this.maxWidth,
          penColor: this.penColor,
        });
      }

      // Adjust coordinate system for high DPI devices
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext("2d")!.scale(ratio, ratio);
    }
  }

  handleSave() {
    if (this.signaturePad) {
      const dataUrl = this.signaturePad.toDataURL();
      this.setAttribute("data-url", dataUrl);
      this.requestUpdate();
    }
  }

  handleClear() {
    if (this.signaturePad) {
      this.signaturePad.clear();
      this.removeAttribute("data-url");
      this.requestUpdate();
    }
  }

  handleMouseMove(_event: MouseEvent) {
    // Adjust mouse coordinates based on the canvas position
    this.shadowRoot?.querySelector(".signature-pad") as HTMLCanvasElement | null;
  }

  firstUpdated() {
    this.generateSignaturePad();
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has("minWidth") || changedProperties.has("maxWidth") || changedProperties.has("penColor")) {
      this.generateSignaturePad();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === "attributes" && mutation.attributeName === "class") {
          const target = mutation.target as HTMLElement;
          if (
            (target.classList.contains("show") &&
              (target.classList.contains("modal") || target.classList.contains("collapse"))) ||
            (target.classList.contains("active") && target.classList.contains("nav-link"))
          ) {
            this.generateSignaturePad();
          }
        }
      }
    });

    observer.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ["class"],
    });
  }

  render() {
    let isClear = this.showClear ? "inline-block" : "none";
    let isSave = this.showSave ? "inline-block" : "none";
    return html`
      <div class="pgd-digital-signature">
        <div class="signature-drawer">
          <canvas
            part="areapad"
            class="signature-pad"
            @mousemove="${(e: MouseEvent) => this.handleMouseMove(e)}"
          ></canvas>
        </div>
        <div style="padding-left: 20px;">
          <button
            part="saveBtn"
            class="button signature-btn-save"
            style="display: ${isSave}"
            @click="${this.handleSave}"
          >
            ${this.saveLabelBtn}
          </button>
          <button
            part="clearBtn"
            class="button signature-btn-clear"
            style="display: ${isClear}"
            @click="${this.handleClear}"
          >
            ${this.clearLabelBtn}
          </button>
        </div>
      </div>
    `;
  }
}
